import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { Region, Locale, RegionConfig, REGION_CONFIGS } from './types';

interface I18nContextType {
  region: Region;
  locale: Locale;
  config: RegionConfig;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  isCanada: boolean;
  isUSA: boolean;
  isFrench: boolean;
  availableLocales: Locale[];
  getLocalePath: (path: string, targetLocale?: Locale) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Detect region from hostname
function detectRegion(): Region {
  if (typeof window === 'undefined') return 'US';

  const hostname = window.location.hostname.toLowerCase();
  const path = window.location.pathname;

  // Canadian content is served from mtli.ca and from /en or /fr prefixed paths
  // during prerendering, where the browser runs on localhost/127.0.0.1.
  if (hostname === 'mtli.ca' || hostname.endsWith('.mtli.ca')) return 'CA';
  if (/^\/(en|fr)(\/|$)/.test(path)) return 'CA';

  return 'US';
}

// Detect locale from path
function detectLocaleFromPath(region: Region): Locale {
  if (typeof window === 'undefined') return region === 'US' ? 'en-US' : 'en-CA';
  
  const path = window.location.pathname;
  
  // For Canada, check for /fr prefix
  if (region === 'CA') {
    if (path.startsWith('/fr')) return 'fr-CA';
    return 'en-CA';
  }
  
  return 'en-US';
}

// Get browser language preference
function getBrowserLanguage(): string {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language?.split('-')[0] || 'en';
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [region] = useState<Region>(detectRegion);
  const [locale, setLocaleState] = useState<Locale>(() => detectLocaleFromPath(region));
  
  // Handle locale changes and URL updates
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    
    // Update URL for Canadian site language switch
    if (region === 'CA') {
      const currentPath = window.location.pathname;
      const cleanPath = currentPath.replace(/^\/(en|fr)/, '');
      const newPath = newLocale === 'fr-CA' ? `/fr${cleanPath || '/'}` : `/en${cleanPath || '/'}`;
      window.history.pushState({}, '', newPath);
    }
  };
  
  // Load translations lazily
  const [translations, setTranslations] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        let module;
        if (locale === 'fr-CA') {
          module = await import('./content/fr-CA/common.ts');
        } else if (locale === 'en-US') {
          module = await import('./content/en-US/common.ts');
        } else {
          module = await import('./content/en-CA/common.ts');
        }
        setTranslations(module.default || {});
      } catch (e) {
        console.error('Failed to load translations for', locale, e);
        try {
          const fallback = await import('./content/en-CA/common.ts');
          setTranslations(fallback.default || {});
        } catch {
          setTranslations({});
        }
      }
    };
    loadTranslations();
  }, [locale]);
  
  const t = (key: string): string => {
    return translations[key] || key;
  };
  
  const getLocalePath = (path: string, targetLocale?: Locale): string => {
    const loc = targetLocale || locale;
    const cleanPath = path.replace(/^\/(en|fr)/, '');
    
    if (region === 'US') {
      return cleanPath || '/';
    }
    
    // For Canada, add language prefix
    if (loc === 'fr-CA') {
      return `/fr${cleanPath || '/'}`;
    }
    return `/en${cleanPath || '/'}`;
  };
  
  const config = REGION_CONFIGS[region];
  
  const availableLocales: Locale[] = region === 'CA' ? ['en-CA', 'fr-CA'] : ['en-US'];
  
  const value = useMemo(() => ({
    region,
    locale,
    config,
    setLocale,
    t,
    isCanada: region === 'CA',
    isUSA: region === 'US',
    isFrench: locale === 'fr-CA',
    availableLocales,
    getLocalePath,
  }), [region, locale, config, translations]);
  
  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export { I18nContext };
